import axios from 'axios'
import { startSubmit, stopSubmit, reset } from 'redux-form'
import { takeLatest, call, put, all, take, select } from 'redux-saga/effects'
import history from 'configureHistory'
import { articleTypes, articleActions } from 'features/articles'
import { socketActions } from 'features/socket'
import {
	authTypes,
	authActions,
	setToken,
	unsetToken,
	setTheme,
	setScheme,
} from '.'

const getRoute = state => {
	if (
		state.router.location.state &&
		state.router.location.state.from.search.includes('?d=/')
	) {
		return state.router.location.state.from.search.replace('?d=', '')
	}

	return null
}

function* putToken({ token }) {
	yield all([call(setToken, token), put(socketActions.startChannel())])
}

const jumpToHome = () => {
	window.location.href = '/'
}

function* loginRequest({ credentials }) {
	yield put(startSubmit('login'))

	try {
		const { data } = yield call(axios.post, 'auth/login', credentials)

		yield all([
			put(authActions.loginSuccess(data)),
			put(authActions.putToken(data.token)),
			call(setTheme, data.theme),
			call(setScheme, data.team.colorScheme),
		])

		yield put(articleActions.getArticlesRequest())

		const route = yield select(getRoute)

		if (route) {
			yield call(history.replace, route)
		}

		yield put(stopSubmit('login'))
	} catch (e) {
		if (!e.response) {
			yield put(stopSubmit('login', { _error: 'connection_failed' }))
		} else if (e.response.data.statusCode === 423) {
			yield call(history.replace, {
				pathname: '/login',
				state: {
					awaitingVerification: true,
					awaitingVerificationEmail: credentials.email,
				},
			})
			yield put(stopSubmit('login'))
		} else {
			yield put(stopSubmit('login', { _error: e.response.data.message }))

			// yield put(stopSubmit('login', {
			//     email: 'Email does not exist',
			//     password: 'Wrong password'
			// }))
		}
	}
}

function* logoutRequest() {
	try {
		yield call(axios.get, 'auth/logout')
		yield put(authActions.logoutSuccess())

		yield call(unsetToken)
		yield call(jumpToHome)
	} catch (e) {
		yield call(unsetToken)
		yield put(authActions.logoutSuccess())
		yield call(jumpToHome)
	}
}

function* registerRequest({ data }) {
	yield put(startSubmit('register'))

	try {
		const response = yield call(axios.post, 'auth/register', data)

		if (response.data.isVerified) {
			yield all([
				put(authActions.loginSuccess(response.data)),
				put(authActions.putToken(response.data.token)),
			])

			yield put(articleActions.getArticlesRequest())
		} else {
			// yield put(authActions.loginSuccess(response.data))

			yield call(history.replace, {
				pathname: '/login',
				state: {
					awaitingVerification: true,
					awaitingVerificationEmail: data.email,
				},
			})

			yield call(window.scrollTo, 0, 0)
		}

		yield put(stopSubmit('register'))
	} catch (error) {
		if (!error.response) {
			yield put(stopSubmit('register', { _error: 'connection_failed' }))
		} else if (error.response.status === 411) {
			yield put(
				stopSubmit('register', {
					password: error.response.data.message,
				})
			)
		} else if (error.response.status === 409) {
			yield put(
				stopSubmit('register', JSON.parse(error.response.data.message))
			)
		} else {
			yield put(
				stopSubmit('register', { _error: error.response.data.message })
			)
		}
	}
}

function* createTeamRequest({ data }) {
	yield put(startSubmit('newTeam'))

	try {
		const { data: response } = yield call(axios.post, 'teams/create', data)

		yield all([
			put(reset('newTeam')),
			put(authActions.createTeamSuccess(response)),
			put(authActions.putToken(response.token)),
		])

		yield put(articleActions.getArticlesRequest())
		yield take(articleTypes.GET_ARTICLES_SUCCESS)

		yield put(articleActions.resetArticleState())
		yield put(stopSubmit('newTeam'))
		yield call(history.replace, '/')
	} catch (error) {
		if (!error.response) {
			yield put(stopSubmit('newTeam', { _error: 'connection_failed' }))
		} else {
			yield put(
				stopSubmit('newTeam', JSON.parse(error.response.data.message))
			)
		}
	}
}

function* resendVerificationRequest({ email }) {
	try {
		yield call(axios.post, 'auth/resend-verification', { email })
		yield call(history.replace, {
			pathname: '/login',
			state: {
				awaitingVerification: true,
				resentVerification: true,
			},
		})
	} catch (error) {
		if (!error.response) {
			yield put(stopSubmit('login', { _error: 'connection_failed' }))
		} else {
			yield put(
				stopSubmit('login', { _error: error.response.data.message })
			)
		}
	}
}

function* switchTeamSuccess({ profile }) {
	try {
		yield call(setScheme, profile.team.colorScheme)
	} catch (error) {
		//
	}
}

const authSagas = [
	takeLatest(authTypes.PUT_TOKEN, putToken),
	takeLatest(authTypes.LOGIN_REQUEST, loginRequest),
	takeLatest(authTypes.LOGOUT_REQUEST, logoutRequest),
	takeLatest(authTypes.REGISTER_REQUEST, registerRequest),
	takeLatest(authTypes.CREATE_TEAM_REQUEST, createTeamRequest),
	takeLatest(
		authTypes.RESEND_VERIFICATION_REQUEST,
		resendVerificationRequest
	),
	takeLatest(authTypes.SWITCH_TEAM_SUCCESS, switchTeamSuccess),
]

export default authSagas
